import { createFileRoute, Link } from "@tanstack/react-router";
import { withMemberGate } from "@/lib/with-member-gate";
import { useEffect, useMemo, useRef, useState } from "react";
import { claimAudio, registerAudioOwner, type AudioOwner } from "@/lib/audio-bus";
import { getAudioContext, resumeAudio, unlockAudio } from "@/lib/audio-context";
import {
  Headphones,
  Play,
  Pause,
  SkipForward,
  SkipBack,
  Heart,
  Search,
  X,
  Gauge,
  Mic,
  MicOff,
  Volume2,
  VolumeX,
  Waves,
  CloudRain,
  Flame,
  TreePine,
  Wind,
  CircleSlash,
  Download,
  Sparkles,
  Clock,
  Star,
  RotateCcw,
} from "lucide-react";
import { toggleFavorite } from "@/lib/favorites";
import { useFavoritesList } from "@/hooks/useFavorite";
import { useComfortPrefs } from "@/lib/preferences";
import { TtsPrefetchBadge } from "@/components/TtsPrefetchBadge";

export const Route = createFileRoute("/meditations")({
  validateSearch: (search: Record<string, unknown>): { open?: string } => {
    const out: { open?: string } = {};
    if (typeof search.open === "string") out.open = search.open;
    return out;
  },
  head: () => ({
    meta: [
      { name: "robots", content: "noindex" },
      { title: "Guided Meditations — Recovery Trauma™" },
      {
        name: "description",
        content:
          "A trauma-informed meditation library: grounding, nervous system regulation, sleep, self-compassion and inner child practices. Choose your voice, pace and background sound.",
      },
      { property: "og:title", content: "Guided Meditations — Recovery Trauma™" },
      {
        property: "og:description",
        content:
          "Choice-led, trauma-informed meditations with adjustable voice, speed and background sound.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recoverytrauma.com/meditations" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/meditations" }],
  }),
  component: withMemberGate(MeditationsPage, "Meditations"),
});

/* --------------------------- DATA --------------------------- */

type Topic =
  | "all"
  | "anxiety"
  | "sleep"
  | "nervous-system"
  | "grounding"
  | "emotional"
  | "self-worth"
  | "inner-child"
  | "compassion"
  | "stress"
  | "trauma"
  | "morning"
  | "evening"
  | "grief"
  | "safety";

const TOPICS: { id: Topic; label: string; emoji: string; color: string }[] = [
  { id: "all", label: "All", emoji: "✨", color: "bg-cream text-foreground border-gold/40" },
  { id: "anxiety", label: "Anxiety Relief", emoji: "🌿", color: "bg-[oklch(0.94_0.04_150)] text-[oklch(0.35_0.06_150)] border-[oklch(0.75_0.06_150)]" },
  { id: "sleep", label: "Sleep & Relaxation", emoji: "🌙", color: "bg-[oklch(0.93_0.04_265)] text-[oklch(0.32_0.08_265)] border-[oklch(0.7_0.07_265)]" },
  { id: "nervous-system", label: "Nervous System", emoji: "🌊", color: "bg-[oklch(0.93_0.05_200)] text-[oklch(0.32_0.08_200)] border-[oklch(0.7_0.08_200)]" },
  { id: "grounding", label: "Grounding", emoji: "🍃", color: "bg-[oklch(0.93_0.05_140)] text-[oklch(0.32_0.08_140)] border-[oklch(0.7_0.08_140)]" },
  { id: "emotional", label: "Emotional Regulation", emoji: "💗", color: "bg-[oklch(0.94_0.04_15)] text-[oklch(0.35_0.07_15)] border-[oklch(0.75_0.07_15)]" },
  { id: "self-worth", label: "Self-Worth", emoji: "⭐", color: "bg-[oklch(0.94_0.05_85)] text-[oklch(0.35_0.08_85)] border-[oklch(0.75_0.08_85)]" },
  { id: "inner-child", label: "Inner Child", emoji: "🧸", color: "bg-[oklch(0.94_0.04_50)] text-[oklch(0.35_0.07_50)] border-[oklch(0.75_0.07_50)]" },
  { id: "compassion", label: "Compassion", emoji: "🤍", color: "bg-[oklch(0.94_0.03_315)] text-[oklch(0.35_0.06_315)] border-[oklch(0.75_0.06_315)]" },
  { id: "stress", label: "Stress Relief", emoji: "💨", color: "bg-[oklch(0.93_0.04_220)] text-[oklch(0.32_0.07_220)] border-[oklch(0.7_0.07_220)]" },
  { id: "trauma", label: "Trauma Recovery", emoji: "🕊", color: "bg-[oklch(0.93_0.04_290)] text-[oklch(0.32_0.07_290)] border-[oklch(0.7_0.07_290)]" },
  { id: "morning", label: "Morning Support", emoji: "☀️", color: "bg-[oklch(0.95_0.06_95)] text-[oklch(0.4_0.09_95)] border-[oklch(0.8_0.09_95)]" },
  { id: "evening", label: "Evening Wind Down", emoji: "🌆", color: "bg-[oklch(0.93_0.04_30)] text-[oklch(0.35_0.07_30)] border-[oklch(0.75_0.07_30)]" },
  { id: "grief", label: "Grief & Loss", emoji: "🤲", color: "bg-[oklch(0.93_0.03_250)] text-[oklch(0.32_0.06_250)] border-[oklch(0.7_0.06_250)]" },
  { id: "safety", label: "Safety & Stabilization", emoji: "🛡", color: "bg-[oklch(0.94_0.04_180)] text-[oklch(0.32_0.07_180)] border-[oklch(0.7_0.07_180)]" },
];

type Difficulty = "Beginner" | "Gentle" | "Intermediate";

type Meditation = {
  id: string;
  title: string;
  blurb: string;
  topic: Exclude<Topic, "all">;
  minutes: number;
  difficulty: Difficulty;
  beginner?: boolean;
  script: string[];
};

const MEDITATIONS: Meditation[] = [
  {
    id: "two-min-grounding",
    title: "2-Minute Grounding",
    blurb: "For moments of overwhelm.",
    topic: "grounding",
    minutes: 2,
    difficulty: "Beginner",
    beginner: true,
    script: [
      "Welcome. You don't have to close your eyes if that doesn't feel safe.",
      "Feel your feet on the floor. Notice the weight of your body, supported.",
      "Name five things you can see around you, slowly.",
      "Now four things you can touch, even just the fabric on your skin.",
      "Three things you can hear in this moment.",
      "Two things you can smell, or imagine smelling.",
      "And one slow breath, in and out. You are here. You are okay.",
    ],
  },
  {
    id: "finding-safety",
    title: "Finding Safety in the Present Moment",
    blurb: "A gentle stabilization practice.",
    topic: "safety",
    minutes: 6,
    difficulty: "Gentle",
    beginner: true,
    script: [
      "Settle into a position that feels supportive. Adjust as much as you need.",
      "Let your gaze soften, or close your eyes only if that feels okay.",
      "Notice the surface beneath you holding your weight without effort.",
      "Bring to mind a place — real or imagined — where you have felt safe.",
      "Let yourself see the colors, hear the sounds, feel the temperature there.",
      "If safety is hard to access, that's okay. Even a tiny sense of okay-ness is enough.",
      "Place a hand wherever it feels comforting. Breathe slowly into that warmth.",
      "When you're ready, gently bring yourself back to the room.",
    ],
  },
  {
    id: "calming-ns",
    title: "Calming the Nervous System",
    blurb: "Designed for activation and stress.",
    topic: "nervous-system",
    minutes: 8,
    difficulty: "Gentle",
    beginner: true,
    script: [
      "Begin by lengthening your exhale. Breathe in for four, out for six.",
      "Imagine the exhale as a soft tide leaving the shore.",
      "Let your shoulders drop. Unclench your jaw. Soften your tongue.",
      "Hum quietly if it feels okay — even a small hum tones the vagus nerve.",
      "Move slowly: roll your head, stretch, sigh out loud.",
      "Your body is allowed to settle. There's nothing to fix right now.",
      "Stay with the long, slow exhale for as long as you need.",
    ],
  },
  {
    id: "before-sleep",
    title: "Before Sleep Meditation",
    blurb: "Helps prepare the body and mind for rest.",
    topic: "sleep",
    minutes: 12,
    difficulty: "Gentle",
    beginner: true,
    script: [
      "Lie down in a way that feels supportive. Adjust your pillow, your blanket.",
      "Let the day be done. There is nothing more to solve tonight.",
      "Breathe in softly through the nose, out slowly through the mouth.",
      "Begin a body scan from your feet. Soften the feet, the ankles, the calves.",
      "Move slowly upward — thighs, hips, belly, chest, shoulders.",
      "Let your arms be heavy. Let your jaw be heavy. Let your eyes rest.",
      "If thoughts come, that's okay. Let them drift like clouds across the sky.",
      "You are safe to rest now.",
    ],
  },
  {
    id: "self-compassion",
    title: "Self-Compassion Meditation",
    blurb: "Supporting kindness toward yourself.",
    topic: "compassion",
    minutes: 10,
    difficulty: "Gentle",
    beginner: true,
    script: [
      "Place a hand over your heart, or wherever feels comforting.",
      "Take a breath, and silently say: this is a moment of difficulty.",
      "Difficulty is part of being human. You are not alone in this.",
      "Now offer yourself a phrase: may I be gentle with myself.",
      "May I give myself the kindness I need.",
      "May I remember that I am worthy of care, exactly as I am.",
      "Let the warmth of your own hand remind you: you are here for yourself.",
    ],
  },
  {
    id: "inner-child",
    title: "Inner Child Meditation",
    blurb: "A gentle reconnection practice.",
    topic: "inner-child",
    minutes: 12,
    difficulty: "Intermediate",
    beginner: true,
    script: [
      "Imagine a safe, soft place — a meadow, a sunlit room, a quiet beach.",
      "Invite a younger version of yourself to join you there. Any age that comes.",
      "Notice them. What are they wearing? What is their expression?",
      "There's no rush. Simply be present with them.",
      "If it feels right, kneel down, or sit beside them. Let them speak, or stay quiet.",
      "Offer the words you needed to hear back then: you are safe now. I see you. I'm here.",
      "When it feels complete, place a hand on your own heart, and return.",
    ],
  },
  {
    id: "emotional-release",
    title: "Emotional Release Meditation",
    blurb: "Helping you create space for difficult emotions.",
    topic: "emotional",
    minutes: 10,
    difficulty: "Intermediate",
    beginner: true,
    script: [
      "Settle in. Notice without judgment what's present in your body right now.",
      "Is there tightness? Heaviness? A buzzing? A heat?",
      "Place a hand near the sensation. Breathe slowly into that area.",
      "You don't need to push the feeling away. You don't need to fix it.",
      "Whisper: I see you. You're allowed to be here.",
      "Tears, sighs, movement — anything that arises is welcome.",
      "When you're ready, take three slow breaths and gently open your eyes.",
    ],
  },
  {
    id: "morning-arrival",
    title: "Morning Arrival",
    blurb: "A soft start to your day.",
    topic: "morning",
    minutes: 5,
    difficulty: "Beginner",
    script: [
      "Before checking your phone or rushing into the day, pause here.",
      "Stretch slowly. Notice where your body wants to move.",
      "Place a hand on your chest and silently say: I am here.",
      "Set one intention — soft, simple. Maybe: today I move at my own pace.",
      "Take three slow breaths, and begin your day from this grounded place.",
    ],
  },
  {
    id: "evening-wind-down",
    title: "Evening Wind Down",
    blurb: "Closing the day with kindness.",
    topic: "evening",
    minutes: 7,
    difficulty: "Beginner",
    script: [
      "The day is winding down. Let your shoulders drop.",
      "Acknowledge anything you carried today — even quietly, in your mind.",
      "Place a hand on your heart. Whisper: thank you, body, for moving me through today.",
      "Notice three small things that were okay, even tiny ones.",
      "Now release the day. Tomorrow can wait.",
    ],
  },
  {
    id: "grief-holding",
    title: "Holding Grief",
    blurb: "A practice for loss, ache, and tenderness.",
    topic: "grief",
    minutes: 9,
    difficulty: "Gentle",
    script: [
      "Find a private, supportive place. Allow whatever is here to be here.",
      "Grief is love that has no place to land. Let it move through you.",
      "Place a hand on your heart, the other on your belly.",
      "Breathe slowly. There is no timeline for grief.",
      "Whisper the name of what you've lost, if it feels right.",
      "You are not alone in this. Many hands, across time, have held what you are holding.",
    ],
  },
  {
    id: "anxiety-anchor",
    title: "Anxiety Anchor",
    blurb: "A quick anchor when anxiety rises.",
    topic: "anxiety",
    minutes: 4,
    difficulty: "Beginner",
    script: [
      "Press your feet firmly into the floor. Feel the resistance back.",
      "Take a breath in for four, hold for two, out for six.",
      "Name what you are: I am safe in this moment. I am okay right now.",
      "Look around. Find one warm color. One soft texture. One steady sound.",
      "Your nervous system is doing its job. You don't have to fight it — just slow with it.",
    ],
  },
  {
    id: "stress-soft-reset",
    title: "Stress Soft Reset",
    blurb: "A soft 5-minute pause whenever you need one.",
    topic: "stress",
    minutes: 5,
    difficulty: "Beginner",
    script: [
      "Step away from what you're doing — there's nothing you need to push through.",
      "Stand or sit, however your body feels supported. Roll your shoulders back, then let them drop.",
      "Take three sighing exhales. Loud is welcome. Messy is welcome.",
      "Place a hand on your belly. Breathe in slowly. Out slowly.",
      "When you're ready, return to your day from a softer place.",
    ],
  },
];

/* --------------------------- VOICES --------------------------- */

import {
  VOICE_IDS as EL_VOICE_IDS,
  type VoicePref as ElVoicePref,
  prefetchTts,
  cancelTts as cancelElTts,
  abortPendingPrefetches,
  ttsKey,
  hasCachedTts,
  isPrefetchingTts,
  useTts,
  getPrefetchWindow,
  setPrefetchPlaybackSpeed,
  recordSegmentDuration,
  logSegmentEvent,
} from "@/lib/elevenVoice";

type VoicePref = ElVoicePref | "none";

const VOICE_OPTIONS: { id: VoicePref; label: string; sublabel: string }[] = [
  { id: "female", label: "Gentle Female Voice", sublabel: "Soft, warm tone" },
  
  { id: "none", label: "No Voice", sublabel: "Background sound or silence only" },
];

/* --------------------------- BACKGROUND SOUNDS (WebAudio) --------------------------- */

type Ambience = "silent" | "ocean" | "rain" | "fire" | "forest" | "wind";

const AMBIENCE_OPTIONS: { id: Ambience; label: string; icon: typeof Waves }[] = [
  { id: "silent", label: "Silent", icon: CircleSlash },
  { id: "ocean", label: "Ocean Waves", icon: Waves },
  { id: "rain", label: "Rain", icon: CloudRain },
  { id: "fire", label: "Campfire", icon: Flame },
  { id: "forest", label: "Forest", icon: TreePine },
  { id: "wind", label: "Soft Wind", icon: Wind },
];

function useAmbience(active: Ambience, enabled: boolean, volume: number) {
  const ctxRef = useRef<AudioContext | null>(null);
  const nodesRef = useRef<{ stop: () => void; gain: GainNode; base: number } | null>(null);

  useEffect(() => {
    if (!enabled || active === "silent") {
      nodesRef.current?.stop();
      nodesRef.current = null;
      return;
    }
    // Shared platform context — ambience runs alongside voice playback and
    // must not open a context of its own (iOS concurrency limit).
    const ctx = getAudioContext();
    if (!ctx) return;
    resumeAudio();
    ctxRef.current = ctx;

    const bufferSize = 2 * ctx.sampleRate;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    let lastOut = 0;
    for (let i = 0; i < bufferSize; i++) {
      const white = Math.random() * 2 - 1;
      data[i] = (lastOut + 0.02 * white) / 1.02;
      lastOut = data[i];
      data[i] *= 3.5;
    }

    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.loop = true;

    const filter = ctx.createBiquadFilter();
    const gain = ctx.createGain();
    let base = 0.18;

    const lfo = ctx.createOscillator();
    const lfoGain = ctx.createGain();
    lfo.connect(lfoGain);

    switch (active) {
      case "ocean":
        filter.type = "lowpass"; filter.frequency.value = 600;
        lfo.frequency.value = 0.12; lfoGain.gain.value = 0.12;
        lfoGain.connect(gain.gain); base = 0.22;
        break;
      case "rain":
        filter.type = "highpass"; filter.frequency.value = 1200;
        base = 0.14;
        break;
      case "fire":
        filter.type = "lowpass"; filter.frequency.value = 800;
        lfo.frequency.value = 6; lfoGain.gain.value = 0.04;
        lfoGain.connect(gain.gain); base = 0.15;
        break;
      case "forest":
        filter.type = "bandpass"; filter.frequency.value = 900; filter.Q.value = 0.6;
        base = 0.12;
        break;
      case "wind":
        filter.type = "lowpass"; filter.frequency.value = 400;
        lfo.frequency.value = 0.25; lfoGain.gain.value = 0.08;
        lfoGain.connect(gain.gain); base = 0.18;
        break;
    }
    gain.gain.value = base * volume;

    src.connect(filter); filter.connect(gain); gain.connect(ctx.destination);
    src.start(); lfo.start();
    if (ctx.state === "suspended") ctx.resume().catch(() => {});

    nodesRef.current = {
      gain, base,
      stop: () => {
        try { src.stop(); } catch { /* noop */ }
        try { lfo.stop(); } catch { /* noop */ }
        src.disconnect(); filter.disconnect(); gain.disconnect();
      },
    };

    return () => {
      nodesRef.current?.stop();
      nodesRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, enabled]);

  useEffect(() => {
    const n = nodesRef.current;
    if (n) n.gain.gain.value = n.base * volume;
  }, [volume]);
}

/* --------------------------- PAGE --------------------------- */

function MeditationsPage() {
  const search = Route.useSearch();
  const [topic, setTopic] = useState<Topic>("all");
  const [query, setQuery] = useState("");
  const [active, setActive] = useState<Meditation | null>(null);
  const [favOnly, setFavOnly] = useState(false);
  const [favorites, setFavorites] = useState<string[]>(() => {
    if (typeof window === "undefined") return [];
    try { return JSON.parse(localStorage.getItem("rt:med:favs") || "[]"); } catch { return []; }
  });
  const [continueId, setContinueId] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    return localStorage.getItem("rt:med:continue");
  });

  useEffect(() => { localStorage.setItem("rt:med:favs", JSON.stringify(favorites)); }, [favorites]);

  // Deep-link support: /meditations?open=<id> opens that meditation.
  useEffect(() => {
    if (!search.open) return;
    const m = MEDITATIONS.find((x) => x.id === search.open);
    if (m) setActive(m);
  }, [search.open]);

  const comfort = useComfortPrefs();
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return MEDITATIONS.filter((m) => {
      if (comfort.hiddenTopics.includes(m.topic)) return false;
      if (topic !== "all" && m.topic !== topic) return false;
      if (!q) return true;
      return [m.title, m.blurb, m.topic].join(" ").toLowerCase().includes(q);
    });
  }, [topic, query, comfort.hiddenTopics]);

  const favList = useFavoritesList("rt:fav-meditations");
  const favIds = useMemo(() => new Set(favList.map((f) => f.id)), [favList]);
  const visible = favOnly ? filtered.filter((m) => favIds.has(m.id)) : filtered;

  const startHereSeries = MEDITATIONS.filter((m) => m.beginner);

  const toggleFav = (id: string) => {
    const med = MEDITATIONS.find((m) => m.id === id);
    setFavorites((f) => (f.includes(id) ? f.filter((x) => x !== id) : [...f, id]));
    // Mirror to unified favourites hub so /favorites can list it
    if (med) {
      toggleFavorite("rt:fav-meditations", {
        id: med.id,
        label: med.title,
        sub: `${med.minutes} min · ${med.topic}`,
        to: "/meditations",
      });
    }
  };

  const [pendingInnerChild, setPendingInnerChild] = useState<Meditation | null>(null);

  const startMeditation = (m: Meditation) => {
    setActive(m);
    setContinueId(m.id);
    localStorage.setItem("rt:med:continue", m.id);
  };

  const openMeditation = (m: Meditation) => {
    if (m.topic === "inner-child") {
      setPendingInnerChild(m);
      return;
    }
    startMeditation(m);
  };

  const nextMeditation = () => {
    if (!active) return;
    const list = filtered.length ? filtered : MEDITATIONS;
    const idx = list.findIndex((m) => m.id === active.id);
    const next = list[(idx + 1) % list.length];
    if (next) openMeditation(next);
  };
  const prevMeditation = () => {
    if (!active) return;
    const list = filtered.length ? filtered : MEDITATIONS;
    const idx = list.findIndex((m) => m.id === active.id);
    const prev = list[(idx - 1 + list.length) % list.length];
    if (prev) openMeditation(prev);
  };

  const continueMed = continueId ? MEDITATIONS.find((m) => m.id === continueId) : null;

  return (
    <div className="mx-auto max-w-6xl px-4 py-10">
      {/* Header */}
      <section className="text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--gradient-gold)] shadow-[var(--shadow-gold)]">
          <Headphones className="h-6 w-6 text-foreground" />
        </div>
        <p className="mt-4 text-xs font-medium uppercase tracking-[0.18em] text-[var(--gold-deep)]">
          Guided Meditations
        </p>
        <h1 className="mt-2 font-display text-4xl text-foreground md:text-5xl">
          Trauma-Informed Guided Meditations
        </h1>
        <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">
          Trauma-informed, choice-led practices. Pick a topic, choose a voice that feels safe,
          and add a background sound if it helps. There is no right way to do this.
        </p>
      </section>

      {/* Search */}
      <div className="mx-auto mt-8 max-w-2xl">
        <div className="relative">
          <Search className="pointer-events-none absolute start-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search meditations — grounding, sleep, anxiety…"
            className="w-full rounded-full border border-gold/30 bg-card py-3 ps-11 pe-11 text-sm text-foreground shadow-sm outline-none transition-all focus:border-gold/60 focus:shadow-[var(--shadow-gold)]"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="absolute end-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-muted-foreground hover:bg-cream"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Topic chips */}
      <div className="mt-6 -mx-4 overflow-x-auto px-4 pb-2 [scrollbar-width:thin]">
        <div className="flex min-w-max gap-2">
          {TOPICS.map((t) => {
            const sel = topic === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTopic(t.id)}
                className={`group flex items-center gap-2 whitespace-nowrap rounded-full border px-4 py-2 text-sm font-semibold transition-all ${
                  sel
                    ? `${t.color} scale-[1.04] shadow-[var(--shadow-gold)] ring-2 ring-gold/30`
                    : `${t.color} opacity-70 hover:opacity-100 hover:scale-[1.02]`
                }`}
              >
                <span aria-hidden>{t.emoji}</span>
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Continue listening */}
      {continueMed && (
        <section className="mt-8 rounded-3xl border border-gold/30 bg-[var(--gradient-gold)]/20 p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-[var(--gold-deep)]">
                Continue Listening
              </p>
              <p className="mt-1 font-display text-xl text-foreground">{continueMed.title}</p>
              <p className="text-sm text-muted-foreground">{continueMed.blurb}</p>
            </div>
            <button
              type="button"
              onClick={() => openMeditation(continueMed)}
              className="btn-gold"
            >
              <Play className="h-4 w-4" /> Resume
            </button>
          </div>
        </section>
      )}

      {/* Gentle Starting Points */}
      {topic === "all" && !query && !favOnly && (
        <section className="mt-10">
          <div className="mb-4 flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-[var(--gold-deep)]" />
            <h2 className="font-display text-xl uppercase tracking-[0.18em] text-[var(--gold-deep)]">
              🌿 Gentle Starting Points
            </h2>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {startHereSeries.slice(0, 6).map((m) => (
              <MeditationCard
                key={m.id}
                m={m}
                fav={favorites.includes(m.id)}
                onPlay={() => openMeditation(m)}
                onFav={() => toggleFav(m.id)}
              />
            ))}
          </div>
        </section>
      )}

      {/* Library */}
      <section className="mt-12">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-display text-xl uppercase tracking-[0.18em] text-[var(--gold-deep)]">
            {favOnly ? "Your Favourites" : topic === "all" ? "Full Library" : TOPICS.find((t) => t.id === topic)?.label}
          </h2>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setFavOnly((v) => !v)}
              aria-pressed={favOnly}
              className={`inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                favOnly
                  ? "border-rose-300 bg-rose-50 text-rose-600"
                  : "border-border bg-card text-muted-foreground hover:border-rose-200 hover:text-rose-500"
              }`}
            >
              <Heart className={`h-3.5 w-3.5 ${favOnly ? "fill-current" : ""}`} />
              Favourites only
            </button>
            <span className="text-xs text-muted-foreground">
              {visible.length} {visible.length === 1 ? "meditation" : "meditations"}
            </span>
          </div>
        </div>

        {visible.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-gold/30 bg-card p-10 text-center">
            <p className="text-muted-foreground">
              {favOnly ? "You haven't saved any meditations yet. Tap the heart on a practice to save it." : "No meditations match this filter yet."}
            </p>
            <button
              type="button"
              onClick={() => { setTopic("all"); setQuery(""); setFavOnly(false); }}
              className="mt-3 text-sm font-semibold text-[var(--gold-deep)] hover:underline"
            >
              Clear filters
            </button>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {visible.map((m) => (
              <MeditationCard
                key={m.id}
                m={m}
                fav={favorites.includes(m.id)}
                onPlay={() => openMeditation(m)}
                onFav={() => toggleFav(m.id)}
              />
            ))}
          </div>
        )}
      </section>


      {/* Pair-with footer */}
      <section className="mt-16 rounded-3xl border border-border/50 bg-card p-6">
        <p className="text-xs font-semibold uppercase tracking-wider text-[var(--gold-deep)]">
          Pair Meditation With
        </p>
        <h3 className="mt-1 font-display text-2xl text-foreground">
          A whole companion for your recovery
        </h3>
        <div className="mt-4 flex flex-wrap gap-2">
          {[
            { to: "/journal", label: "Journal" },
            { to: "/dreams", label: "Dream Journal" },
            { to: "/", label: "Breathing" },
            { to: "/tapping", label: "Tapping" },
            { to: "/sleep-dreams", label: "Sleep Support" },
            { to: "/somatic", label: "Somatic Tools" },
            { to: "/shadow-work", label: "Shadow Work" },
            { to: "/community", label: "Community" },
            { to: "/events", label: "Live Events" },
          ].map((p) => (
            <Link
              key={p.to}
              to={p.to}
              className="rounded-full border border-gold/30 bg-cream/60 px-4 py-1.5 text-sm font-semibold text-foreground transition-all hover:scale-[1.02] hover:border-gold/50 hover:bg-cream"
            >
              {p.label}
            </Link>
          ))}
        </div>
      </section>

      {active && (
        <Player
          meditation={active}
          onClose={() => setActive(null)}
          onNext={nextMeditation}
          onPrev={prevMeditation}
          fav={favorites.includes(active.id)}
          onFav={() => toggleFav(active.id)}
        />
      )}

      {pendingInnerChild && (
        <InnerChildAdvisory
          meditation={pendingInnerChild}
          onContinue={() => {
            const m = pendingInnerChild;
            setPendingInnerChild(null);
            startMeditation(m);
            try { localStorage.setItem("rt:med:inner-child-seen", "1"); } catch {}
          }}
          onChooseAnother={() => {
            setPendingInnerChild(null);
            setTopic("all");
          }}
          isFirstTime={
            typeof window !== "undefined" &&
            !localStorage.getItem("rt:med:inner-child-seen")
          }
        />
      )}
    </div>
  );
}

/* ------------------ INNER CHILD ADVISORY ------------------ */

function InnerChildAdvisory({
  meditation,
  onContinue,
  onChooseAnother,
  isFirstTime,
}: {
  meditation: Meditation;
  onContinue: () => void;
  onChooseAnother: () => void;
  isFirstTime: boolean;
}) {
  const [learnMore, setLearnMore] = useState(false);
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="ic-advisory-title"
      className="fixed inset-0 z-[60] flex items-end justify-center bg-black/50 p-0 backdrop-blur-sm sm:items-center sm:p-4"
    >
      <div className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-3xl border border-gold/30 bg-card p-6 shadow-[var(--shadow-gold)] sm:rounded-3xl">
        <div className="mb-4 flex items-center gap-3">
          <span className="grid h-12 w-12 place-items-center rounded-full bg-[var(--gradient-gold)] text-2xl" aria-hidden>
            🧸
          </span>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--gold-deep)]">
              Before you begin
            </p>
            <h2 id="ic-advisory-title" className="font-display text-xl text-foreground">
              A gentle note about Inner Child practice
            </h2>
          </div>
        </div>

        <div className="space-y-3 text-sm leading-relaxed text-foreground/85">
          <p>
            Inner Child practices can sometimes bring up strong emotions, memories, or feelings
            connected to earlier life experiences.
          </p>
          <p>Please move at your own pace and only continue if it feels manageable for you.</p>
          {learnMore && (
            <div className="space-y-3 rounded-2xl border border-border/50 bg-background/50 p-4 text-[13px] text-foreground/80">
              <p>
                If you are currently working through significant trauma, distressing memories, or
                emotional overwhelm, you may wish to explore these practices alongside a qualified
                therapist, counsellor, or trauma-informed professional.
              </p>
              <p>You are welcome to pause, stop, or choose another practice at any time.</p>
              <p className="text-muted-foreground">
                This meditation is designed for reflection and wellbeing and is not a substitute
                for therapy, mental health treatment, diagnosis, or crisis support.
              </p>
            </div>
          )}
          <button
            type="button"
            onClick={() => setLearnMore((v) => !v)}
            className="text-[12px] font-semibold uppercase tracking-wider text-[var(--gold-deep)] underline-offset-4 hover:underline"
          >
            {learnMore ? "Show less" : "Learn more"}
          </button>
        </div>

        {isFirstTime && (
          <div className="mt-5 rounded-2xl border border-lavender/30 bg-lavender/10 p-4">
            <p className="text-[13px] font-medium text-foreground">
              This is your first Inner Child practice.
            </p>
            <p className="mt-1 text-[12px] text-muted-foreground">
              Would you like to begin with a grounding exercise first?
            </p>
            <Link
              to="/grounding"
              className="mt-3 inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border border-gold/40 bg-card px-4 py-2 text-[12px] font-semibold text-foreground transition hover:bg-cream"
            >
              🌿 Start Grounding First
            </Link>
          </div>
        )}

        <div className="mt-6 flex flex-col gap-2 sm:flex-row">
          <button
            type="button"
            onClick={onContinue}
            className="btn-gold flex-1 rounded-full px-5 py-2.5 text-sm font-semibold"
          >
            Continue · {meditation.title}
          </button>
          <button
            type="button"
            onClick={onChooseAnother}
            className="flex-1 rounded-full border border-border bg-card px-5 py-2.5 text-sm font-semibold text-foreground transition hover:bg-background"
          >
            Choose another practice
          </button>
        </div>
      </div>
    </div>
  );
}

/* --------------------------- CARD --------------------------- */

function MeditationCard({
  m, fav, onPlay, onFav,
}: { m: Meditation; fav: boolean; onPlay: () => void; onFav: () => void }) {
  const topicMeta = TOPICS.find((t) => t.id === m.topic)!;
  return (
    <div className="group relative flex flex-col rounded-2xl border border-border/50 bg-card p-5 transition-all hover:-translate-y-0.5 hover:border-gold/40 hover:shadow-[var(--shadow-gold)]">
      <div className="flex items-start justify-between gap-2">
        <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${topicMeta.color}`}>
          <span aria-hidden>{topicMeta.emoji}</span>
          {topicMeta.label}
        </span>
        <button
          type="button"
          onClick={onFav}
          aria-pressed={fav}
          aria-label={fav ? "Remove from favorites" : "Save to favorites"}
          className={`rounded-full p-1.5 transition-colors ${fav ? "text-rose-500" : "text-muted-foreground hover:text-rose-400"}`}
        >
          <Heart className={`h-4 w-4 ${fav ? "fill-current" : ""}`} />
        </button>
      </div>

      <h3 className="mt-3 font-display text-xl text-foreground">{m.title}</h3>
      <p className="mt-1 text-sm text-muted-foreground">{m.blurb}</p>

      <div className="mt-4 flex items-center gap-3 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {m.minutes} min</span>
      </div>


      <button
        type="button"
        onClick={onPlay}
        className="mt-5 inline-flex items-center justify-center gap-2 rounded-full bg-foreground/90 px-4 py-2.5 text-sm font-semibold text-background transition-transform hover:scale-[1.02]"
      >
        <Play className="h-4 w-4" /> Play
      </button>
    </div>
  );
}

/* --------------------------- PLAYER --------------------------- */

const DEFAULTS = {
  voicePref: "female" as VoicePref,
  ambience: "silent" as Ambience,
  voiceOn: true,
  voiceVolume: 1,
  bgVolume: 1,
  speed: 0.95,
};

function Player({
  meditation, onClose, onNext, onPrev,
  fav, onFav,
}: {
  meditation: Meditation;
  onClose: () => void;
  onNext: () => void;
  onPrev: () => void;
  fav: boolean;
  onFav: () => void;
}) {
  // Settings live inside the player so each meditation starts fresh
  const [voicePref, setVoicePref] = useState<VoicePref>(DEFAULTS.voicePref);
  const [ambience, setAmbience] = useState<Ambience>(DEFAULTS.ambience);
  const [voiceOn, setVoiceOn] = useState<boolean>(DEFAULTS.voiceOn);
  const [voiceVolume, setVoiceVolume] = useState<number>(DEFAULTS.voiceVolume);
  const [bgVolume, setBgVolume] = useState<number>(DEFAULTS.bgVolume);
  const [speed, setSpeed] = useState<number>(DEFAULTS.speed);
  const { caller: ttsCaller } = useTts();

  const [hasStarted, setHasStarted] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [stepIdx, setStepIdx] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  // iOS Safari ignores HTMLMediaElement.volume, so the voice track is routed
  // through the shared Web Audio context: a per-element MediaElementSource
  // feeds a GainNode that carries the user's voice-volume setting.
  const voiceGainRef = useRef<GainNode | null>(null);
  const voiceWiredElRef = useRef<HTMLAudioElement | null>(null);
  const playingRef = useRef(false);
  const tickRef = useRef<number | null>(null);

  // Prefetch indicator state.
  //   "cached" — next segment ready, playback will be instant
  //   "loading" — next segment is currently being fetched
  //   "live" — last segment had to be synthesised on demand (fallback)
  //   "idle" — nothing to prefetch (last line, or voice off)
  type PrefetchState = "cached" | "loading" | "live" | "idle";
  const [nextState, setNextState] = useState<PrefetchState>("idle");
  const [fallbackFlash, setFallbackFlash] = useState(false);

  useAmbience(ambience, playing, bgVolume);

  // Keep the adaptive prefetch heuristic in sync with the user's tempo.
  useEffect(() => {
    setPrefetchPlaybackSpeed(speed);
  }, [speed]);

  // Warm the TTS cache for the first few script lines whenever the meditation
  // or voice changes. The window auto-tunes to observed synthesis latency.
  useEffect(() => {
    if (voicePref === "none") return;
    const voiceId = EL_VOICE_IDS[voicePref];
    const window = getPrefetchWindow();
    for (let i = 0; i < Math.min(window + 1, meditation.script.length); i++) {
      prefetchTts(ttsCaller, meditation.script[i], voiceId).catch(() => {});
    }
  }, [meditation.id, voicePref, ttsCaller]);

  // Recompute the "next segment" indicator whenever step, voice or the
  // prefetch queue changes. Polls briefly so we notice a fill completing.
  useEffect(() => {
    if (voicePref === "none") { setNextState("idle"); return; }
    const nextText = meditation.script[stepIdx + 1];
    if (!nextText) { setNextState("idle"); return; }
    const voiceId = EL_VOICE_IDS[voicePref];
    const compute = (): PrefetchState => {
      if (hasCachedTts(nextText, voiceId)) return "cached";
      if (isPrefetchingTts(nextText, voiceId)) return "loading";
      return "idle";
    };
    setNextState(compute());
    const iv = window.setInterval(() => setNextState(compute()), 400);
    return () => window.clearInterval(iv);
  }, [stepIdx, voicePref, meditation.id, meditation.script]);

  // Route a voice element through a GainNode so volume/mute work on iOS.
  // When wired, the element's own volume stays at 1 and the gain carries the
  // setting; when the context is unavailable we fall back to element.volume.
  const wireVoiceGain = (audio: HTMLAudioElement) => {
    const ctx = getAudioContext();
    if (!ctx) return;
    if (voiceWiredElRef.current === audio && voiceGainRef.current) {
      voiceGainRef.current.gain.value = voiceOn ? voiceVolume : 0;
      audio.volume = 1;
      audio.muted = false;
      return;
    }
    try {
      const src = ctx.createMediaElementSource(audio);
      const gain = ctx.createGain();
      gain.gain.value = voiceOn ? voiceVolume : 0;
      src.connect(gain);
      gain.connect(ctx.destination);
      voiceWiredElRef.current = audio;
      voiceGainRef.current = gain;
      audio.volume = 1;
      audio.muted = false;
    } catch {
      // Element already wired or context unavailable — element.volume fallback stays.
    }
  };

  const stopAudio = () => {
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch {}
      audioRef.current.onended = null;
      audioRef.current = null;
    }
    if (voiceGainRef.current) {
      try { voiceGainRef.current.disconnect(); } catch {}
      voiceGainRef.current = null;
    }
    voiceWiredElRef.current = null;
    cancelElTts();
    // Drop any awaiters for the previous playback position — the underlying
    // fetches still finish so anything the user comes back to stays cached.
    abortPendingPrefetches();
  };

  // Stop everything when meditation changes or unmounts
  useEffect(() => {
    setPlaying(false);
    playingRef.current = false;
    setProgress(0);
    setStepIdx(0);
    stopAudio();
    return () => {
      stopAudio();
      if (tickRef.current) window.clearInterval(tickRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meditation.id]);

  const totalSeconds = meditation.minutes * 60;

  const speakStep = async (idx: number, preCreated?: HTMLAudioElement) => {
    if (typeof window === "undefined") return;
    if (idx >= meditation.script.length) {
      setPlaying(false);
      playingRef.current = false;
      setProgress(1);
      return;
    }
    const text = meditation.script[idx];
    if (voicePref === "none") {
      // No narration — advance through steps on a timed cadence so progress feels natural.
      const perStep = Math.max(4000, (totalSeconds * 1000) / Math.max(1, meditation.script.length));
      setStepIdx(idx);
      window.setTimeout(() => {
        if (!playingRef.current) return;
        const nextIdx = idx + 1;
        if (nextIdx < meditation.script.length) {
          void speakStep(nextIdx);
        } else {
          setPlaying(false);
          playingRef.current = false;
          setProgress(1);
        }
      }, perStep);
      return;
    }
    const voiceId = EL_VOICE_IDS[voicePref];
    // Create the Audio element synchronously when possible so playback retains
    // its user-gesture provenance (iOS Safari blocks Audio created after await).
    const audio = preCreated ?? new Audio();
    audio.volume = voiceVolume;
    audio.muted = !voiceOn;
    wireVoiceGain(audio);
    audio.playbackRate = speed;
    if (audioRef.current && audioRef.current !== audio) {
      try { audioRef.current.pause(); } catch {}
      audioRef.current.onended = null;
    }
    audioRef.current = audio;
    try {
      // Fallback detection: if this segment isn't cached, we're waiting on a
      // live server synthesis — flash the "Live" indicator until it resolves.
      const wasCached = hasCachedTts(text, voiceId);
      logSegmentEvent({
        type: "segment:change",
        route: `meditations/${meditation.id}`,
        from: audioRef.current ? idx - 1 : null,
        to: idx,
        text,
      });
      if (!wasCached) setFallbackFlash(true);
      const url = await prefetchTts(ttsCaller, text, voiceId);
      if (!wasCached) setFallbackFlash(false);
      if (!playingRef.current || audioRef.current !== audio) return;
      audio.src = url;
      const playStart = typeof performance !== "undefined" ? performance.now() : Date.now();
      audio.onended = () => {
        if (!playingRef.current) return;
        // Feed observed segment duration back into the adaptive window.
        if (Number.isFinite(audio.duration) && audio.duration > 0) {
          recordSegmentDuration((audio.duration / Math.max(0.25, speed)) * 1000);
        }
        logSegmentEvent({
          type: "segment:end",
          route: `meditations/${meditation.id}`,
          idx,
          durationMs: Math.round(
            (typeof performance !== "undefined" ? performance.now() : Date.now()) - playStart,
          ),
        });
        const nextIdx = idx + 1;
        setStepIdx(nextIdx);
        if (nextIdx < meditation.script.length) {
          window.setTimeout(() => {
            if (playingRef.current) void speakStep(nextIdx);
          }, 900);
        } else {
          setPlaying(false);
          playingRef.current = false;
          setProgress(1);
        }
      };
      await audio.play();
      logSegmentEvent({
        type: "segment:play",
        route: `meditations/${meditation.id}`,
        idx,
        cached: wasCached,
      });
      // Warm the cache for the next N segments — N auto-tunes to observed
      // synthesis latency and current playback speed.
      const lookahead = getPrefetchWindow();
      for (let ahead = 1; ahead <= lookahead; ahead++) {
        const nextText = meditation.script[idx + ahead];
        if (nextText) prefetchTts(ttsCaller, nextText, voiceId).catch(() => {});
      }
    } catch (err) {
      console.error("Meditation TTS error", err);
      setFallbackFlash(false);
      setPlaying(false);
      playingRef.current = false;
    }
  };

  // Voice-change guard.
  //
  // The LRU TTS cache is already segregated by `${voiceId}::${text}`, so a
  // new voice never reads bytes from an old-voice entry. The remaining
  // failure mode is the currently-loaded `<audio>` element: it still holds
  // the previous voice's data URL and would keep speaking the current
  // segment in the old voice after the user switches. On any real voicePref
  // change we abort pending old-voice prefetches, hard-stop the loaded
  // audio, and re-invoke `speakStep(stepIdx)` so the current segment
  // resynthesises (or hits the cache) with the new voice.
  const prevVoiceRef = useRef(voicePref);
  useEffect(() => {
    const prev = prevVoiceRef.current;
    if (prev === voicePref) return;
    prevVoiceRef.current = voicePref;

    abortPendingPrefetches();
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch {}
      audioRef.current.onended = null;
      audioRef.current.src = "";
      audioRef.current = null;
    }
    cancelElTts();

    if (playingRef.current && voicePref !== "none") {
      void speakStep(stepIdx);
    }
    // stepIdx is intentionally read from the closure at fire time; adding
    // it to deps would restart playback on every segment advance.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voicePref]);



  // This player owns a whole spoken sequence, so the global audio bus stops it
  // through `pause()` rather than just pausing the current segment.
  const pauseRef = useRef<() => void>(() => {});
  const ownerRef = useRef<AudioOwner>({
    stop: () => pauseRef.current(),
    ownsElement: (el) => audioRef.current === el,
  });
  useEffect(() => registerAudioOwner(ownerRef.current), []);

  const start = () => {
    // Only one track may sound at a time platform-wide.
    claimAudio(ownerRef.current);
    // Arm the shared Web Audio context from the tap so background ambience
    // is audible on iOS, not just the <audio> voice track.
    void unlockAudio();
    setHasStarted(true);
    setPlaying(true);
    playingRef.current = true;
    if (audioRef.current && audioRef.current.paused && audioRef.current.currentTime > 0 && audioRef.current.src) {
      audioRef.current.playbackRate = speed;
      void audioRef.current.play().catch((err) => console.error("Resume failed", err));
    } else {
      // Create the Audio element NOW inside the click handler so iOS Safari
      // treats the eventual play() as user-initiated. Then hand it to speakStep.
      stopAudio();
      const audio = new Audio();
      audio.volume = voiceVolume;
      audio.muted = !voiceOn;
      wireVoiceGain(audio);
      audio.playbackRate = speed;
      audioRef.current = audio;
      void speakStep(stepIdx, audio);
    }
    if (tickRef.current) window.clearInterval(tickRef.current);
    const startedAt = Date.now() - progress * totalSeconds * 1000;
    tickRef.current = window.setInterval(() => {
      const elapsed = (Date.now() - startedAt) / 1000;
      setProgress(Math.min(1, elapsed / totalSeconds));
    }, 500);
  };

  const pause = () => {
    setPlaying(false);
    playingRef.current = false;
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch {}
    }
    if (tickRef.current) { window.clearInterval(tickRef.current); tickRef.current = null; }
  };
  pauseRef.current = pause;

  const rewarmFrom = (idx: number) => {
    if (voicePref === "none") return;
    const voiceId = EL_VOICE_IDS[voicePref];
    const lookahead = getPrefetchWindow();
    // Keep only prefetches for the new focal window (current + next N).
    const offsets = Array.from({ length: lookahead + 1 }, (_, i) => i);
    const keep = offsets
      .map((i) => meditation.script[idx + i])
      .filter((t): t is string => !!t)
      .map((t) => ttsKey(t, voiceId));
    abortPendingPrefetches({ keep });
    for (const offset of offsets) {
      const t = meditation.script[idx + offset];
      if (t) prefetchTts(ttsCaller, t, voiceId).catch(() => {});
    }
  };

  const skipFwd = () => {
    stopAudio();
    const newIdx = Math.min(meditation.script.length - 1, stepIdx + 1);
    setStepIdx(newIdx);
    setProgress(Math.min(1, (newIdx / meditation.script.length)));
    rewarmFrom(newIdx);
    if (playingRef.current) void speakStep(newIdx);
  };
  const skipBack = () => {
    stopAudio();
    const newIdx = Math.max(0, stepIdx - 1);
    setStepIdx(newIdx);
    setProgress(Math.max(0, (newIdx / meditation.script.length)));
    rewarmFrom(newIdx);
    if (playingRef.current) void speakStep(newIdx);
  };

  // Apply speed changes to currently-playing audio
  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = speed;
  }, [speed]);

  // Live voice volume / mute updates — through the GainNode when the voice
  // element is wired (required on iOS), otherwise via element.volume.
  useEffect(() => {
    const wired =
      voiceGainRef.current && voiceWiredElRef.current === audioRef.current;
    if (wired && voiceGainRef.current) {
      voiceGainRef.current.gain.value = voiceOn ? voiceVolume : 0;
      if (audioRef.current) {
        audioRef.current.volume = 1;
        audioRef.current.muted = false;
      }
    } else if (audioRef.current) {
      audioRef.current.volume = voiceVolume;
      audioRef.current.muted = !voiceOn;
    }
  }, [voiceVolume, voiceOn]);

  const resetSettings = () => {
    pause();
    stopAudio();
    if (tickRef.current) { window.clearInterval(tickRef.current); tickRef.current = null; }
    setVoicePref(DEFAULTS.voicePref);
    setAmbience(DEFAULTS.ambience);
    setVoiceOn(DEFAULTS.voiceOn);
    setVoiceVolume(DEFAULTS.voiceVolume);
    setBgVolume(DEFAULTS.bgVolume);
    setSpeed(DEFAULTS.speed);
    setProgress(0);
    setStepIdx(0);
    setHasStarted(false);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto overscroll-contain bg-black/40 backdrop-blur-sm sm:items-center sm:p-4"
      onClick={onClose}
      role="button"
      tabIndex={-1}
      aria-label="Close player"
      onKeyDown={(e) => { if (e.key === "Escape") onClose(); }}
    >
      <div
        className="relative max-h-[92dvh] w-full max-w-2xl overflow-y-auto overscroll-contain rounded-t-3xl border border-gold/30 bg-card p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] shadow-2xl sm:max-h-[88dvh] sm:rounded-3xl"
        onClick={(e) => e.stopPropagation()}
      >

        <button
          type="button"
          onClick={onClose}
          className="absolute end-4 top-4 rounded-full p-2 text-muted-foreground hover:bg-cream"
          aria-label="Close player"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex items-start gap-4">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-[var(--gradient-gold)] shadow-[var(--shadow-gold)]">
            <Headphones className="h-6 w-6 text-foreground" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold uppercase tracking-wider text-[var(--gold-deep)]">
              Now Playing
            </p>
            <h3 className="font-display text-2xl text-foreground">{meditation.title}</h3>
            <p className="text-sm text-muted-foreground">{meditation.blurb}</p>
            {/* Labelled favourite control — kept clear of the Close (X)
                button so the two never overlap on small screens, and so it
                reads as a favourites action rather than a mystery icon. */}
            <button
              type="button"
              onClick={onFav}
              aria-pressed={fav}
              className={`mt-3 inline-flex min-h-10 items-center gap-1.5 rounded-full border px-4 py-1.5 text-xs font-semibold transition-colors ${
                fav
                  ? "border-rose-300 bg-rose-500/10 text-rose-500"
                  : "border-border bg-card text-muted-foreground hover:text-rose-400"
              }`}
            >
              <Heart className={`h-3.5 w-3.5 ${fav ? "fill-current" : ""}`} />
              {fav ? "Saved to Favourites" : "Save to Favourites"}
            </button>
          </div>
        </div>

        {/* Before You Begin — small intro card, shown until user starts */}
        {!hasStarted && (
          <div className="mt-5 rounded-2xl border border-gold/30 bg-[var(--gradient-gold)]/15 p-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-[var(--gold-deep)]">
              💛 Before You Begin
            </p>
            <p className="mt-2 text-sm leading-relaxed text-foreground/85">
              Choose a voice and background sound if you would like. Find a comfortable position.
              There is no right way to do this. Press play whenever you are ready.
            </p>
            <button
              type="button"
              onClick={start}
              className="btn-gold mt-3 inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold"
            >
              <Play className="h-4 w-4" /> Begin Meditation
            </button>
          </div>
        )}

        {/* Current line */}
        <div className="mt-5 rounded-2xl bg-cream/60 p-4 text-center text-foreground/80">
          <p className="text-sm leading-relaxed">
            {meditation.script[Math.min(stepIdx, meditation.script.length - 1)]}
          </p>
        </div>

        {/* Progress */}
        <div className="mt-5">
          <div className="h-2 w-full overflow-hidden rounded-full bg-cream">
            <div
              className="h-full bg-[var(--gradient-gold)] transition-[width] duration-500"
              style={{ width: `${Math.round(progress * 100)}%` }}
            />
          </div>
          <div className="mt-1 flex justify-between text-xs text-muted-foreground">
            <span>{formatTime(progress * totalSeconds)}</span>
            <span>{formatTime(totalSeconds)}</span>
          </div>
        </div>

        {/* Controls */}
        <div className="mt-4 flex items-center justify-center gap-3">
          <button
            type="button" onClick={skipBack}
            className="rounded-full p-3 text-foreground hover:bg-cream"
            aria-label="Previous line"
          >
            <SkipBack className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={() => (playing ? pause() : start())}
            className="flex h-14 w-14 items-center justify-center rounded-full bg-foreground text-background shadow-[var(--shadow-gold)] transition-transform hover:scale-[1.04]"
            aria-label={playing ? "Pause" : "Play"}
          >
            {playing ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6 translate-x-0.5" />}
          </button>
          <button
            type="button" onClick={skipFwd}
            className="rounded-full p-3 text-foreground hover:bg-cream"
            aria-label="Next line"
          >
            <SkipForward className="h-5 w-5" />
          </button>
        </div>

        {/* Prefetch indicator — shows whether the next line is ready or
            we had to fall back to on-demand synthesis for this one. */}
        {voicePref !== "none" && hasStarted && (
          <div className="mt-2 flex items-center justify-center">
            <TtsPrefetchBadge next={nextState} fallback={fallbackFlash} />
          </div>
        )}


        {/* Speed */}
        <div className="mt-5 flex items-center gap-3">
          <Gauge className="h-4 w-4 text-muted-foreground" />
          <span className="text-xs font-semibold text-muted-foreground">Speed</span>
          <div className="flex gap-1">
            {[0.8, 0.95, 1.1, 1.25].map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setSpeed(s)}
                className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                  speed === s ? "bg-foreground text-background" : "bg-cream text-foreground hover:bg-gold/20"
                }`}
              >
                {s.toFixed(2)}x
              </button>
            ))}
          </div>
        </div>

        {/* Voice */}
        <div className="mt-5">
          <div className="mb-2 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Mic className="h-4 w-4 text-muted-foreground" />
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Voice</span>
            </div>
            <button
              type="button"
              onClick={() => setVoiceOn((v) => !v)}
              aria-pressed={voiceOn}
              className={`inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-semibold transition-colors ${
                voiceOn
                  ? "border-gold/50 bg-gold/10 text-foreground"
                  : "border-border bg-card text-muted-foreground"
              }`}
            >
              {voiceOn ? <Mic className="h-3 w-3" /> : <MicOff className="h-3 w-3" />}
              Voice {voiceOn ? "On" : "Off"}
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {VOICE_OPTIONS.map((v) => (
              <button
                key={v.id}
                type="button"
                onClick={() => {
                  setVoicePref(v.id);
                  if (v.id === "none") {
                    setVoiceOn(false);
                  } else if (!voiceOn) {
                    setVoiceOn(true);
                  }
                  if (playing) {
                    stopAudio();
                    void speakStep(stepIdx);
                  }
                }}
                aria-pressed={voicePref === v.id}
                className={`rounded-2xl border p-3 text-start transition-all ${
                  voicePref === v.id
                    ? "border-gold/60 bg-gold/10 shadow-sm"
                    : "border-border/50 bg-card hover:border-gold/40"
                }`}
              >
                <p className="text-xs font-semibold text-foreground">
                  {v.id === "female" ? "Female" : "🔇 No Voice"}
                </p>
                <p className="text-[11px] text-muted-foreground">{v.sublabel}</p>
              </button>
            ))}
          </div>
          {voicePref !== "none" ? (
            <div className="mt-3 flex items-center gap-3">
              {voiceOn ? <Volume2 className="h-4 w-4 text-muted-foreground" /> : <VolumeX className="h-4 w-4 text-muted-foreground" />}
              <span className="w-28 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Voice Volume</span>
              <input
                type="range"
                min={0}
                max={1}
                step={0.01}
                value={voiceVolume}
                onChange={(e) => setVoiceVolume(parseFloat(e.target.value))}
                className="flex-1 accent-[var(--gold-deep)]"
                aria-label="Voice volume"
              />
              <span className="w-9 text-end text-[11px] tabular-nums text-muted-foreground">
                {Math.round(voiceVolume * 100)}%
              </span>
            </div>
          ) : (
            <p className="mt-3 text-[11px] italic text-muted-foreground">
              No spoken guidance — enjoy only the background sounds or silence.
            </p>
          )}
        </div>

        {/* Ambience */}
        <div className="mt-5">
          <div className="mb-2 flex items-center gap-2">
            <Waves className="h-4 w-4 text-muted-foreground" />
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Background Sound
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {AMBIENCE_OPTIONS.map((a) => {
              const Icon = a.icon;
              const sel = ambience === a.id;
              return (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => setAmbience(a.id)}
                  className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold transition-all ${
                    sel
                      ? "border-gold/60 bg-gold/15 text-foreground shadow-sm scale-[1.03]"
                      : "border-border/50 bg-card text-muted-foreground hover:border-gold/40 hover:text-foreground"
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {a.label}
                </button>
              );
            })}
          </div>
          <div className="mt-3 flex items-center gap-3">
            <Volume2 className="h-4 w-4 text-muted-foreground" />
            <span className="w-28 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Background Volume</span>
            <input
              type="range"
              min={0}
              max={1}
              step={0.01}
              value={bgVolume}
              onChange={(e) => setBgVolume(parseFloat(e.target.value))}
              disabled={ambience === "silent"}
              className="flex-1 accent-[var(--gold-deep)] disabled:opacity-40"
              aria-label="Background sound volume"
            />
            <span className="w-9 text-end text-[11px] tabular-nums text-muted-foreground">
              {Math.round(bgVolume * 100)}%
            </span>
          </div>
        </div>

        {/* Reset */}
        <div className="mt-5 flex justify-center">
          <button
            type="button"
            onClick={resetSettings}
            className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-card px-4 py-2 text-xs font-semibold text-foreground transition hover:border-gold/40 hover:bg-cream"
          >
            <RotateCcw className="h-3.5 w-3.5" /> Reset Settings
          </button>
        </div>

        {/* Footer actions */}
        <div className="mt-5 flex items-center justify-between gap-2">
          <button
            type="button" onClick={onPrev}
            className="text-xs font-semibold text-muted-foreground hover:text-foreground"
          >
            ← Previous track
          </button>
          <button
            type="button"
            disabled
            className="inline-flex cursor-not-allowed items-center gap-1 rounded-full border border-border/50 px-3 py-1 text-[11px] font-semibold text-muted-foreground/70"
            title="Coming soon"
          >
            <Download className="h-3 w-3" /> Download (soon)
          </button>
          <button
            type="button" onClick={onNext}
            className="text-xs font-semibold text-muted-foreground hover:text-foreground"
          >
            Next track →
          </button>
        </div>
      </div>
    </div>
  );
}

function formatTime(sec: number) {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}
